'use client';

import { Box, chakra, Flex, Input, Popover, Portal, Text } from '@chakra-ui/react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LuChevronDown, LuPlus } from 'react-icons/lu';

export interface EntityOption {
  id: string;
  label: string;
  secondary?: string;
}

interface Props {
  value?: string;
  options: EntityOption[];
  placeholder: string;
  createLabel: string; // e.g. "customer" — used in "Add customer" / "Create …"
  onSelect: (id: string) => void;
  onCreate?: (query: string) => void;
  size?: 'sm' | 'md';
}

const EntityPicker = memo(
  ({ value, options, placeholder, createLabel, onSelect, onCreate, size = 'sm' }: Props) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const searchRef = useRef<HTMLInputElement>(null);

    const selected = options.find((option) => option.id === value);

    const filtered = useMemo(() => {
      const q = query.trim().toLowerCase();
      if (q === '') {
        return options;
      }
      return options.filter(
        (option) =>
          option.label.toLowerCase().includes(q) ||
          (option.secondary ?? '').toLowerCase().includes(q),
      );
    }, [options, query]);

    const exactMatch = useMemo(
      () => options.some((option) => option.label.toLowerCase() === query.trim().toLowerCase()),
      [options, query],
    );

    const onOpenChange = useCallback((details: { open: boolean }) => {
      setOpen(details.open);
      if (!details.open) {
        setQuery('');
      }
    }, []);
    const onSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);
    }, []);
    const onPick = useCallback(
      (id: string) => () => {
        onSelect(id);
        setOpen(false);
        setQuery('');
      },
      [onSelect],
    );
    const onCreateClick = useCallback(() => {
      onCreate?.(query.trim());
      setOpen(false);
      setQuery('');
    }, [onCreate, query]);

    useEffect(() => {
      if (!open) {
        return undefined;
      }
      const id = requestAnimationFrame(() => searchRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }, [open]);

    return (
      <Popover.Root open={open} onOpenChange={onOpenChange} positioning={{ placement: 'bottom-start' }}>
        <Popover.Trigger asChild>
          <chakra.button
            type="button"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            w="full"
            borderWidth="1px"
            borderColor="border.light"
            borderRadius="md"
            px={3}
            h={size === 'sm' ? '32px' : '40px'}
            fontSize="sm"
            bg="surface.background"
            cursor="pointer"
            _hover={{ bg: 'surface.muted' }}
          >
            <Text color={selected != null ? 'text.primary' : 'text.placeholder'} truncate>
              {selected?.label ?? placeholder}
            </Text>
            <LuChevronDown size={14} />
          </chakra.button>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner>
            <Popover.Content width="320px" bg="surface.background" borderColor="border.light" borderRadius="lg" boxShadow="md" p={1}>
              <Box p={1}>
                <Input
                  ref={searchRef}
                  placeholder="Search…"
                  size="sm"
                  borderColor="border.light"
                  borderRadius="md"
                  value={query}
                  onChange={onSearch}
                />
              </Box>
              <Box maxH="240px" overflowY="auto">
                {filtered.map((option) => (
                  <Flex
                    key={option.id}
                    direction="column"
                    px={2}
                    py={1.5}
                    borderRadius="md"
                    cursor="pointer"
                    _hover={{ bg: 'surface.muted' }}
                    onClick={onPick(option.id)}
                  >
                    <Text fontSize="sm" fontWeight="medium">
                      {option.label}
                    </Text>
                    {option.secondary != null && (
                      <Text fontSize="xs" color="text.secondary">
                        {option.secondary}
                      </Text>
                    )}
                  </Flex>
                ))}
                {filtered.length === 0 && (
                  <Text fontSize="xs" color="text.secondary" px={2} py={2}>
                    No matches
                  </Text>
                )}
              </Box>
              {onCreate != null && (
                <Flex
                  alignItems="center"
                  gap={2}
                  px={2}
                  py={2}
                  mt={1}
                  borderTopWidth="1px"
                  borderColor="border.light"
                  color="selection.600"
                  cursor="pointer"
                  _hover={{ bg: 'surface.muted' }}
                  borderRadius="md"
                  onClick={onCreateClick}
                >
                  <LuPlus size={14} />
                  <Text fontSize="sm" fontWeight="medium">
                    {query.trim() !== '' && !exactMatch ? `Create “${query.trim()}”` : `Add ${createLabel}`}
                  </Text>
                </Flex>
              )}
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
    );
  },
);
EntityPicker.displayName = 'EntityPicker';

export default EntityPicker;
